/* eslint-disable react-refresh/only-export-components -- component library with sub-components */
import { Check, ChevronRight, DotsVertical } from '@untitledui/icons';
import {
  cloneElement,
  createContext,
  type FC,
  isValidElement,
  type ReactElement,
  type ReactNode,
  type RefAttributes,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type {
  ButtonProps as AriaButtonProps,
  MenuItemProps as AriaMenuItemProps,
  MenuProps as AriaMenuProps,
  MenuTriggerProps as AriaMenuTriggerProps,
  PopoverProps as AriaPopoverProps,
  SeparatorProps as AriaSeparatorProps,
} from 'react-aria-components';
import {
  Button as AriaButton,
  Header as AriaHeader,
  Menu as AriaMenu,
  MenuItem as AriaMenuItem,
  MenuSection as AriaMenuSection,
  MenuTrigger as AriaMenuTrigger,
  OverlayTriggerStateContext,
  Popover as AriaPopover,
  Separator as AriaSeparator,
  SubmenuTrigger as AriaSubmenuTrigger,
} from 'react-aria-components';

import { useStackingZIndex } from '@/contexts/StackingContext';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { Dialog, Modal, ModalOverlay } from '@/lib/base/overlays/modal';
import { PortalEventBoundary } from '@/lib/base/overlays/portal-event-boundary';
import { useDismissOnInteractOutside } from '@/lib/base/overlays/useDismissOnInteractOutside';
import { Tooltip } from '@/lib/base/tooltip/tooltip';
import { cx } from '@/utils/cx';

interface DropdownContextValue {
  instance?: string;
  isOpen: boolean;
}
const DropdownContext = createContext<DropdownContextValue | null>(null);
function useDropdownContext(): DropdownContextValue {
  return useContext(DropdownContext) ?? { instance: undefined, isOpen: false };
}

interface DropdownItemProps extends AriaMenuItemProps {
  /** The label of the item to be displayed. */
  label?: string;
  /** An addon to be displayed on the right side of the item. */
  addon?: string;
  /** If true, the item will not have any styles. */
  unstyled?: boolean;
  /** An icon to be displayed on the left side of the item. */
  icon?: FC<{ className?: string }>;
  /** Tooltip text shown when the item is disabled */
  disabledTooltip?: string;
  /**
   * When set, the item renders as an `<a>` and integrates with the app
   * RouterProvider for client-side navigation; modified/middle clicks open a
   * new tab natively.
   */
  href?: AriaMenuItemProps['href'];
  /** Click handler, forwarded to react-aria's `useMenuItem` `onClickProp`. */
  onClick?: (e: React.MouseEvent) => void;
  /**
   * Render the item in the active/open-record state: brand-tinted inner row,
   * icon, and text, paired with an `aria-current` accessible-state signal so
   * assistive tech can tell which row corresponds to the record currently
   * open. This is a VISUAL + accessible state (distinct from react-aria list
   * selection / `selectedKeys`) used to mark a menu row whose corresponding
   * record is currently open. Defaults to false.
   */
  isActive?: boolean;
}

const DropdownItem = ({
  label,
  children,
  addon,
  icon: Icon,
  unstyled,
  disabledTooltip,
  isActive,
  ...props
}: DropdownItemProps) => {
  // react-aria-components' MenuItem runs every prop through filterDOMProps
  // before spreading onto the DOM node, and that filter only allowlists a
  // fixed set of global/labelling attributes — an `aria-current` prop is
  // silently dropped, never reaching the element. Set it imperatively via
  // ref so screen readers actually see it; the JSX prop below is kept for
  // type-level documentation of the contract but has no runtime effect.
  const setActiveAriaState = useCallback(
    (node: HTMLElement | null) => {
      if (!node) {
        return;
      }
      if (isActive) {
        node.setAttribute('aria-current', 'true');
      } else {
        node.removeAttribute('aria-current');
      }
    },
    [isActive]
  );

  if (unstyled) {
    return <AriaMenuItem id={label} textValue={label} {...props} />;
  }

  return (
    <AriaMenuItem
      {...props}
      ref={setActiveAriaState}
      aria-current={isActive ? 'true' : undefined}
      className={state =>
        cx(
          'group block cursor-pointer px-1.5 py-px outline-hidden',
          state.isDisabled && 'cursor-not-allowed',
          typeof props.className === 'function'
            ? props.className(state)
            : props.className
        )
      }
    >
      {state => {
        const inner = (
          <div
            data-testid='dropdown-item-inner'
            className={cx(
              'relative flex items-center rounded-md px-2.5 py-2 outline-focus-ring transition duration-100 ease-linear',
              state.isSelected && 'bg-active',
              !state.isDisabled && !isActive && 'group-hover:bg-primary_hover',
              state.isFocused && !isActive && 'bg-primary_hover',
              state.isFocusVisible && 'outline-2 -outline-offset-2',
              isActive && !state.isDisabled && 'bg-utility-brand-50'
            )}
          >
            {Icon && (
              <Icon
                aria-hidden='true'
                className={cx(
                  'mr-2 size-4 shrink-0 stroke-[2.25px]',
                  state.isDisabled ? 'text-fg-disabled' : 'text-fg-quaternary',
                  isActive && !state.isDisabled && 'text-brand-600'
                )}
              />
            )}

            <span
              className={cx(
                'grow truncate text-sm font-semibold flex items-center gap-2',
                state.isDisabled ? 'text-disabled' : 'text-secondary',
                state.isFocused && 'text-secondary_hover',
                isActive && !state.isDisabled && 'text-brand-600'
              )}
            >
              {label ||
                (typeof children === 'function' ? children(state) : children)}
            </span>

            {addon && (
              <span
                className={cx(
                  'ml-3 shrink-0 rounded px-1 py-px text-xs font-medium ring-1 ring-secondary ring-inset',
                  state.isDisabled ? 'text-disabled' : 'text-quaternary'
                )}
              >
                {addon}
              </span>
            )}

            {state.isSelected && (
              <Check
                aria-hidden='true'
                className={cx(
                  'ml-auto size-4 shrink-0 stroke-[2.5px] text-fg-brand-primary',
                  state.isDisabled && 'text-fg-disabled'
                )}
              />
            )}
          </div>
        );

        // When the item is disabled and has a tooltip, wrap the inner row in
        // an interactive Tooltip. We can't rely on the native HTML `title`
        // attribute here: browsers handle title rendering inconsistently on
        // elements with `aria-disabled='true'` (which is what react-aria sets
        // for disabled menu items, since the MenuItem stays focusable for
        // keyboard navigation). The Tooltip component uses pointer/focus
        // handlers on a wrapper div + a portaled overlay, so it surfaces the
        // explanation regardless of the underlying element's disabled state.
        // `wrapperClassName='block'` overrides the default `inline-flex` so
        // the wrapper still fills the menu row width.
        if (state.isDisabled && disabledTooltip) {
          return (
            <Tooltip
              title={disabledTooltip}
              interactive={true}
              wrapperClassName='block'
              placement='top'
            >
              {inner}
            </Tooltip>
          );
        }

        return inner;
      }}
    </AriaMenuItem>
  );
};

type DropdownMenuProps<T extends object> = AriaMenuProps<T>;

const DropdownMenu = <T extends object>(props: DropdownMenuProps<T>) => {
  return (
    <AriaMenu
      disallowEmptySelection
      selectionMode='single'
      {...props}
      className={state =>
        cx(
          'h-min overflow-y-auto py-1 outline-hidden select-none',
          typeof props.className === 'function'
            ? props.className(state)
            : props.className
        )
      }
    />
  );
};

type DropdownPopoverProps = AriaPopoverProps;

/** Stable no-op for the dismiss hook when no trigger state is in context. */
const NOOP_DISMISS = () => {};

const DropdownPopover = (props: DropdownPopoverProps) => {
  const zIndex = useStackingZIndex();
  const { instance } = useDropdownContext();
  // Non-modal popovers disable react-aria's built-in outside-pointer
  // dismissal; restore it here (full rationale in
  // useDismissOnInteractOutside).
  const popoverRef = useRef<HTMLElement>(null);
  const triggerState = useContext(OverlayTriggerStateContext);
  useDismissOnInteractOutside(
    popoverRef,
    triggerState?.isOpen ?? false,
    triggerState?.close ?? NOOP_DISMISS
  );
  return (
    // The boundary must wrap the AriaPopover ELEMENT (not its children):
    // react-aria's own Escape/keyboard handling lives on the popover root, and
    // React only skips ancestors of the boundary — see portal-event-boundary.
    <PortalEventBoundary>
      <AriaPopover
        ref={popoverRef}
        placement='bottom right'
        shouldFlip
        containerPadding={16}
        // Non-modal by default: react-aria's modal popover dismisses via a
        // full-viewport underlay, but that underlay is rendered with z-index
        // auto — inside any stacked surface (a slideout at z≈810, this popover
        // at z≈900) it paints BENEATH the page, so outside clicks never reach
        // it: the popover can't be dismissed and a second popover can be opened
        // on top (bug: task-sidebar Status + Due date both stuck open).
        // Non-modal skips the underlay entirely and dismisses via a
        // document-level interact-outside listener, which is stacking-
        // independent. Overridable via props for a caller that genuinely needs
        // modal blocking (spread is after this default).
        isNonModal
        {...props}
        data-component='menu'
        data-instance={instance}
        className={state =>
          cx(
            'w-62 origin-(--trigger-anchor-point) overflow-auto rounded-lg bg-primary shadow-lg ring-1 ring-secondary_alt will-change-transform',
            state.isEntering &&
              'duration-150 ease-out animate-in fade-in placement-right:slide-in-from-left-0.5 placement-top:slide-in-from-bottom-0.5 placement-bottom:slide-in-from-top-0.5',
            state.isExiting &&
              'duration-100 ease-in animate-out fade-out placement-right:slide-out-to-left-0.5 placement-top:slide-out-to-bottom-0.5 placement-bottom:slide-out-to-top-0.5',
            typeof props.className === 'function'
              ? props.className(state)
              : props.className
          )
        }
        style={{ zIndex, ...props.style }}
      >
        {state => (
          <>
            <div
              data-component='menu-ready-marker'
              data-instance={instance}
              aria-hidden='true'
              style={{ display: 'none' }}
              data-ready={
                !state.isEntering && !state.isExiting ? '' : undefined
              }
            />
            {typeof props.children === 'function'
              ? props.children(state)
              : props.children}
          </>
        )}
      </AriaPopover>
    </PortalEventBoundary>
  );
};

const DropdownSeparator = (props: AriaSeparatorProps) => {
  return (
    <AriaSeparator
      {...props}
      className={cx('my-1 h-px w-full bg-border-secondary', props.className)}
    />
  );
};

interface DropdownDotsButtonProps
  extends AriaButtonProps, RefAttributes<HTMLButtonElement> {
  /**
   * Glyph size. Defaults to 'sm' (20px), matching IconButton's default so a
   * kebab sits level with the icon buttons beside it. Use 'xs' (16px) in dense
   * chrome — a dashboard tile header, a table row — where IconButton is also
   * 'xs'; the two sizes must be chosen together or the trio goes ragged.
   */
  size?: 'xs' | 'sm';
}

const DropdownDotsButton = ({
  size = 'sm',
  ...props
}: DropdownDotsButtonProps) => {
  const { instance, isOpen } = useDropdownContext();
  return (
    <AriaButton
      {...props}
      data-component='menu-trigger'
      data-instance={instance}
      data-state={isOpen ? 'open' : 'closed'}
      // Honor a caller-supplied aria-label (e.g. 'Record actions') so pages
      // with multiple kebab menus expose distinct accessible names; fall back
      // to the generic 'Open menu' when none is provided.
      aria-label={props['aria-label'] ?? 'Open menu'}
      className={state =>
        cx(
          'cursor-pointer rounded-md text-fg-quaternary outline-focus-ring transition duration-100 ease-linear',
          (state.isPressed || state.isHovered) && 'text-fg-quaternary_hover',
          (state.isPressed || state.isFocusVisible) &&
            'outline-2 outline-offset-2',
          typeof props.className === 'function'
            ? props.className(state)
            : props.className
        )
      }
    >
      <DotsVertical
        className={cx(
          'transition-inherit-all',
          size === 'xs' ? 'size-4' : 'size-5'
        )}
      />
    </AriaButton>
  );
};

interface DropdownSubmenuTriggerProps {
  /** The label of the menu item that opens the submenu. */
  label: string;
  /** An icon to be displayed on the left side of the trigger. */
  icon?: FC<{ className?: string }>;
  /** Whether the submenu trigger is disabled (submenu cannot be opened). */
  isDisabled?: boolean;
  /**
   * Estimated minimum width of the submenu in pixels. Used by the smart-
   * placement algorithm (desktop popover variant) to decide whether the
   * submenu can fit beside the trigger or must fall back to opening below
   * it. Defaults to a value matching `Dropdown.Popover`'s `w-62` (~248px).
   */
  submenuMinWidth?: number;
  /**
   * The submenu content — must be a single `Dropdown.Popover` element with a
   * `Dropdown.Menu` inside, mirroring the top-level dropdown structure. The
   * mobile bottom-sheet variant unwraps the `Popover` and renders the inner
   * `Menu` items inside a `Dialog` styled as a sheet.
   */
  children: ReactElement;
}

/** Minimum gap between submenu and viewport edge before we flip placements. */
const SUBMENU_VIEWPORT_PADDING = 16;
/** Default submenu min-width matches `Dropdown.Popover`'s `w-62` class. */
const DEFAULT_SUBMENU_MIN_WIDTH = 248;
/** Off-center horizontal nudge when opening below — matches the trigger inset. */
const SUBMENU_BELOW_CROSS_OFFSET = -8;

type SubmenuPlacement = 'right top' | 'bottom start';

// =============================================================================
// SubmenuTriggerItem — shared visual MenuItem rendering for the trigger row
// =============================================================================
//
// Both the desktop popover variant and the mobile sheet variant render the
// trigger MenuItem with the same chrome (label, optional leading icon, and a
// trailing chevron-right indicator). Only the activation behavior differs:
// desktop wraps in `AriaSubmenuTrigger` (hover-to-open side popover); mobile
// uses a regular `onAction` that calls into the SubmenuSheetProvider.

interface SubmenuTriggerItemProps {
  label: string;
  icon?: FC<{ className?: string }>;
  isDisabled?: boolean;
  /** Forwarded to the MenuItem's outer node — used by desktop for measuring. */
  innerRef?: React.Ref<HTMLDivElement>;
  /** Activation handler — desktop leaves this unset (AriaSubmenuTrigger handles it); mobile wires it to the sheet provider. */
  onAction?: () => void;
  /** Hover-start handler — desktop uses it to remeasure submenu placement. */
  onHoverStart?: () => void;
  /**
   * Whether to show the trailing chevron-right indicator. Default true. The
   * chevron makes sense when the submenu opens to the side (desktop popover
   * variant) but reads as misleading "drill-in" affordance on mobile, where
   * the submenu opens as a bottom sheet or below the trigger.
   */
  showChevron?: boolean;
}

const SubmenuTriggerItem = ({
  label,
  icon: Icon,
  isDisabled,
  innerRef,
  onAction,
  onHoverStart,
  showChevron = true,
}: SubmenuTriggerItemProps) => {
  return (
    <AriaMenuItem
      ref={innerRef}
      isDisabled={isDisabled}
      textValue={label}
      onAction={onAction}
      onHoverStart={onHoverStart}
      className={state =>
        cx(
          'group block cursor-pointer px-1.5 py-px outline-hidden',
          state.isDisabled && 'cursor-not-allowed'
        )
      }
    >
      {state => (
        <div
          className={cx(
            'relative flex items-center rounded-md px-2.5 py-2 outline-focus-ring transition duration-100 ease-linear',
            !state.isDisabled && 'group-hover:bg-primary_hover',
            state.isFocused && 'bg-primary_hover',
            state.isFocusVisible && 'outline-2 -outline-offset-2'
          )}
        >
          {Icon && (
            <Icon
              aria-hidden='true'
              className={cx(
                'mr-2 size-4 shrink-0 stroke-[2.25px]',
                state.isDisabled ? 'text-fg-disabled' : 'text-fg-quaternary'
              )}
            />
          )}

          <span
            className={cx(
              'grow truncate text-sm font-semibold flex items-center gap-2',
              state.isDisabled ? 'text-disabled' : 'text-secondary',
              state.isFocused && 'text-secondary_hover'
            )}
          >
            {label}
          </span>

          {showChevron && (
            <ChevronRight
              aria-hidden='true'
              className={cx(
                'ml-2 size-4 shrink-0 stroke-[2.25px]',
                state.isDisabled ? 'text-fg-disabled' : 'text-fg-quaternary'
              )}
            />
          )}
        </div>
      )}
    </AriaMenuItem>
  );
};

// =============================================================================
// SubmenuSheetProvider — shared state for mobile bottom-sheet submenus
// =============================================================================
//
// This is the architectural core of the mobile bottom-sheet pattern. The
// provider lives ABOVE the dropdown tree (typically near the app root). When
// the user activates a `Dropdown.SubmenuTrigger` on mobile, the trigger calls
// `openSheet({label, content})` on the provider; the parent dropdown then
// closes (default react-aria menu behavior on item activation), but the sheet
// state is owned by the provider and survives the menu unmounting. The
// provider renders the modal in its own subtree, so the sheet remains open
// until the user picks an item, taps the backdrop, or hits Escape.
//
// If no provider is mounted, `Dropdown.SubmenuTrigger` falls back to its
// popover behavior on mobile — the bottom sheet is opt-in via mounting the
// provider.

interface SubmenuSheetState {
  label: string;
  content: ReactNode;
}

interface DropdownSubmenuSheetContextValue {
  openSheet: (sheet: SubmenuSheetState) => void;
  closeSheet: () => void;
}

const DropdownSubmenuSheetContext =
  createContext<DropdownSubmenuSheetContextValue | null>(null);

interface DropdownSubmenuSheetProviderProps {
  children: ReactNode;
}

/**
 * Provider that enables the mobile bottom-sheet behavior of
 * `Dropdown.SubmenuTrigger`. Mount once near the app root, above any
 * dropdown usage. Without it, mobile falls back to the popover variant.
 */
function DropdownSubmenuSheetProvider({
  children,
}: DropdownSubmenuSheetProviderProps) {
  const [activeSheet, setActiveSheet] = useState<SubmenuSheetState | null>(
    null
  );

  const openSheet = useCallback((sheet: SubmenuSheetState) => {
    setActiveSheet(sheet);
  }, []);

  const closeSheet = useCallback(() => {
    setActiveSheet(null);
  }, []);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        closeSheet();
      }
    },
    [closeSheet]
  );

  const ctx = useMemo(
    () => ({ openSheet, closeSheet }),
    [openSheet, closeSheet]
  );

  return (
    <DropdownSubmenuSheetContext.Provider value={ctx}>
      {children}
      {activeSheet !== null && (
        <ModalOverlay isOpen onOpenChange={handleOpenChange} isDismissable>
          <Modal>
            <Dialog title={activeSheet.label}>{activeSheet.content}</Dialog>
          </Modal>
        </ModalOverlay>
      )}
    </DropdownSubmenuSheetContext.Provider>
  );
}

/** Returns the sheet context, or null if no provider is mounted. */
function useDropdownSubmenuSheetOptional(): DropdownSubmenuSheetContextValue | null {
  return useContext(DropdownSubmenuSheetContext);
}

// =============================================================================
// Build the menu content rendered inside the bottom sheet.
//
// Expected child tree:
//   <Dropdown.Popover>
//     <Dropdown.Menu>
//       ...items (any element with `onAction`, including custom wrappers)...
//     </Dropdown.Menu>
//   </Dropdown.Popover>
//
// We unwrap the Popover and clone the inner Menu with three overrides:
//
//   1. `onAction` — wraps any existing Menu-level onAction to also call
//      `closeSheet`. We hook closeSheet at the MENU level (not per-item) so
//      it fires for any item activation regardless of how the consumer
//      structured their items (e.g. custom `<ColorMenuItem onSelect=...>`
//      wrappers that don't expose `onAction` on the outer element).
//
//   2. `selectionMode='none'` — items act as buttons, not radios. Picking
//      the same color twice should fire onAction both times; with
//      selectionMode='single' the second click would be a no-op.
//
//   3. `className` — drops `Dropdown.Menu`'s default `h-min overflow-y-auto`
//      so the Dialog body's own scroller can take over. The default classes
//      otherwise fight the Dialog's `flex-1 + overflow-y-auto` and the menu
//      content never overflows correctly inside the sheet.
// =============================================================================

const SHEET_MENU_CLASS = 'py-1 outline-hidden select-none';

function buildSheetMenuContent(
  children: ReactNode,
  closeSheet: () => void
): ReactNode {
  if (!isValidElement(children)) {
    return children;
  }
  type WithChildren = { children?: ReactNode };
  type MenuOverrideProps = {
    onAction?: (key: unknown) => void;
    selectionMode?: 'none' | 'single' | 'multiple';
    className?: AriaMenuProps<object>['className'];
  };

  const popoverProps = children.props as WithChildren;
  const menuEl = popoverProps.children;
  if (!isValidElement(menuEl)) {
    return menuEl;
  }

  const originalOnAction = (menuEl.props as MenuOverrideProps).onAction;
  const wrappedOnAction = (key: unknown) => {
    originalOnAction?.(key);
    closeSheet();
  };

  return cloneElement(menuEl as ReactElement<MenuOverrideProps>, {
    onAction: wrappedOnAction,
    selectionMode: 'none',
    className: SHEET_MENU_CLASS,
  });
}

// =============================================================================
// DropdownSubmenuTriggerPopover — desktop (and mobile fallback) implementation
// =============================================================================
//
// Wraps react-aria's built-in `SubmenuTrigger` and adds smart placement based
// on viewport space around the trigger:
//
// - Opens to the side (`right top`) when there's horizontal room — `shouldFlip`
//   on the underlying Popover handles right ↔ left fallback automatically.
// - Falls back to `bottom start` when measurement confirms neither side fits
//   (parent dropdown hugging a viewport edge, or narrow viewport).
//
// Placement is computed eagerly with the right-side default, then verified
// via useLayoutEffect after layout, and re-measured on hover before the
// submenu would open (covers slide-in animation lag).

const DropdownSubmenuTriggerPopover = ({
  label,
  icon,
  isDisabled,
  submenuMinWidth = DEFAULT_SUBMENU_MIN_WIDTH,
  children,
}: DropdownSubmenuTriggerProps) => {
  const isDesktop = useBreakpoint('md');
  const triggerRef = useRef<HTMLDivElement>(null);
  const [placement, setPlacement] = useState<SubmenuPlacement>(
    isDesktop ? 'right top' : 'bottom start'
  );

  const recomputePlacement = useCallback(() => {
    if (!isDesktop) {
      setPlacement('bottom start');
      return;
    }
    const trigger = triggerRef.current;
    if (!trigger) {
      return;
    }
    const rect = trigger.getBoundingClientRect();
    if (rect.width === 0 && rect.height === 0) {
      return;
    }
    const rightSpace = window.innerWidth - rect.right;
    const leftSpace = rect.left;
    const sidePadded = submenuMinWidth + SUBMENU_VIEWPORT_PADDING;

    if (rightSpace >= sidePadded || leftSpace >= sidePadded) {
      setPlacement('right top');
    } else {
      setPlacement('bottom start');
    }
  }, [isDesktop, submenuMinWidth]);

  useLayoutEffect(() => {
    recomputePlacement();
  }, [recomputePlacement]);

  useEffect(() => {
    window.addEventListener('resize', recomputePlacement);
    return () => {
      window.removeEventListener('resize', recomputePlacement);
    };
  }, [recomputePlacement]);

  const handleTriggerEnter = useCallback(() => {
    recomputePlacement();
  }, [recomputePlacement]);

  const enhancedPopover = isValidElement<{
    placement?: AriaPopoverProps['placement'];
    crossOffset?: number;
  }>(children)
    ? cloneElement(children, {
        placement: children.props.placement ?? placement,
        crossOffset:
          children.props.crossOffset ??
          (placement === 'bottom start' ? SUBMENU_BELOW_CROSS_OFFSET : 0),
      })
    : children;

  return (
    <AriaSubmenuTrigger>
      <SubmenuTriggerItem
        label={label}
        icon={icon}
        isDisabled={isDisabled}
        innerRef={triggerRef}
        onHoverStart={handleTriggerEnter}
        showChevron={isDesktop}
      />
      {enhancedPopover}
    </AriaSubmenuTrigger>
  );
};

// =============================================================================
// DropdownSubmenuTriggerSheet — mobile bottom-sheet implementation
// =============================================================================
//
// Activated only when:
//   - Viewport is < md
//   - A `DropdownSubmenuSheetProvider` is mounted somewhere above
//
// The trigger MenuItem fires `openSheet` on activation. The parent dropdown
// closes (default behavior) but the sheet state lives in the provider so it
// survives. The provider renders a `ModalOverlay` + `Modal` + `Dialog` styled
// as a bottom sheet (the existing Modal chrome is already mobile-first
// bottom-sheet at narrow widths).

interface DropdownSubmenuTriggerSheetProps extends DropdownSubmenuTriggerProps {
  sheetContext: DropdownSubmenuSheetContextValue;
}

const DropdownSubmenuTriggerSheet = ({
  label,
  icon,
  isDisabled,
  children,
  sheetContext,
}: DropdownSubmenuTriggerSheetProps) => {
  const { openSheet, closeSheet } = sheetContext;

  const sheetContent = useMemo(
    () => buildSheetMenuContent(children, closeSheet),
    [children, closeSheet]
  );

  const handleOpen = useCallback(() => {
    openSheet({ label, content: sheetContent });
  }, [openSheet, label, sheetContent]);

  return (
    <SubmenuTriggerItem
      label={label}
      icon={icon}
      isDisabled={isDisabled}
      onAction={handleOpen}
      showChevron={false}
    />
  );
};

// =============================================================================
// DropdownSubmenuTrigger — viewport-aware dispatch
// =============================================================================

/**
 * A menu item that opens a sub-list when activated, used to nest a second
 * menu inside a dropdown. The implementation switches based on viewport and
 * provider availability:
 *
 * - **Desktop (≥ md):** side-popover with smart placement (`right top` by
 *   default, `bottom start` fallback when neither side has room). Uses
 *   react-aria's `SubmenuTrigger` for keyboard nav, hover-to-open, and
 *   click-outside dismissal.
 * - **Mobile (< md), provider mounted:** bottom sheet anchored to the
 *   viewport bottom. Tapping an item, the backdrop, or hitting Esc all
 *   dismiss the sheet.
 * - **Mobile (< md), no provider:** falls back to the desktop popover
 *   variant with `bottom start` placement. (Mount
 *   `Dropdown.SubmenuSheetProvider` near your app root for proper sheet
 *   behavior.)
 *
 * The `children` API is identical across modes — pass a single
 * `<Dropdown.Popover><Dropdown.Menu>...items...</Dropdown.Menu></Dropdown.Popover>`.
 * The mobile-sheet variant unwraps the Popover and renders the Menu's items
 * inside a Dialog; each item's `onAction` is wrapped to also close the sheet.
 */
const DropdownSubmenuTrigger = (props: DropdownSubmenuTriggerProps) => {
  const isDesktop = useBreakpoint('md');
  const sheetContext = useDropdownSubmenuSheetOptional();

  if (!isDesktop && sheetContext) {
    return (
      <DropdownSubmenuTriggerSheet {...props} sheetContext={sheetContext} />
    );
  }
  return <DropdownSubmenuTriggerPopover {...props} />;
};

interface DropdownRootProps extends Omit<AriaMenuTriggerProps, 'children'> {
  instance?: string;
  children: ReactNode;
}

const DropdownRoot = ({
  instance,
  isOpen: controlledIsOpen,
  defaultOpen,
  onOpenChange,
  ...props
}: DropdownRootProps) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(
    defaultOpen ?? false
  );
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : uncontrolledOpen;
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!isControlled) {
        setUncontrolledOpen(open);
      }
      onOpenChange?.(open);
    },
    [isControlled, onOpenChange]
  );
  const ctx = useMemo<DropdownContextValue>(
    () => ({ instance, isOpen }),
    [instance, isOpen]
  );
  return (
    <DropdownContext.Provider value={ctx}>
      <AriaMenuTrigger
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        {...props}
      />
    </DropdownContext.Provider>
  );
};

export const Dropdown = {
  Root: DropdownRoot,
  Popover: DropdownPopover,
  Menu: DropdownMenu,
  Section: AriaMenuSection,
  SectionHeader: AriaHeader,
  Item: DropdownItem,
  Separator: DropdownSeparator,
  DotsButton: DropdownDotsButton,
  SubmenuTrigger: DropdownSubmenuTrigger,
  SubmenuSheetProvider: DropdownSubmenuSheetProvider,
};
